import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { InvoiceService } from '../../../invoice/services/invoice.service';
import { UserService } from '../../services/user.service';
import { Invoice } from '../../../invoice/models';
import { SubscriptionService } from '../../services/subscription.service';
import { User } from '../../models';

@Component({
  selector: 'kt-app-invoices',
  imports: [CommonModule, RouterModule],
  templateUrl: './invoices.component.html',
  styleUrl: './invoices.component.scss'
})
export class InvoicesComponent implements OnInit {
  private invoiceService = inject(InvoiceService);
  private userService = inject(UserService);

  invoices = signal<Invoice[]>([]);
  currentUser: User | null = null;
  selectedInvoice: Invoice | null = null;
  showViewModal = false;
  private subscriptionService = inject(SubscriptionService);

  ngOnInit(): void {
    this.currentUser = this.userService.currentUser();
    this.fetchInvoices();
  }

  private isValidUserId(userId: string): boolean {
    return Boolean(userId?.trim());
  }

  private fetchInvoices(): void {
    if (!this.currentUser || !this.isValidUserId(this.currentUser.id)) {
      this.invoices.set([]);
      return;
    }

    this.invoiceService.getInvoicesByUserId(this.currentUser.id).subscribe({
      next: (invoices) => {
        this.invoices.set(invoices);
      },
      error: (error) => {
        console.error('Error fetching invoices:', error);
        this.invoices.set([]);
      }
    });
  }

  openViewModal(invoice: Invoice): void {
    this.selectedInvoice = invoice;
    this.showViewModal = true;
  }

  closeViewModal(): void {
    this.showViewModal = false;
    this.selectedInvoice = null;
  }

  onPdfClick(invoice: Invoice): void {
    if (this.subscriptionService.checkAndShowPremiumForPdf()) {
      this.getPdf(invoice);
    }
  }

  getPdf(invoice: Invoice): void {
    if (!invoice.id) {
      console.warn('Invoice has no id; cannot get PDF.');
      return;
    }
    this.invoiceService.getInvoicePdfById(invoice.id);
  }

  getStatusBadgeClass(status: Invoice['status']): string {
    switch (status) {
      case 'paid':
        return 'bg-success-subtle text-success';
      case 'sent':
        return 'bg-info-subtle text-info';
      case 'overdue':
        return 'bg-warning-subtle text-warning';
      case 'cancelled':
        return 'bg-secondary-subtle text-secondary';
      default:
        return 'bg-danger-subtle text-danger';
    }
  }

  formatDate(value: Date | string | undefined): string {
    if (!value) return '—';
    const d = typeof value === 'string' ? new Date(value) : value;
    return isNaN(d.getTime()) ? '—' : d.toISOString().split('T')[0];
  }

  getInvoiceMetadataText(invoice: Invoice): string {
    if (!invoice) return '';
    const meta = invoice.invoiceMetadata;
    const lines: string[] = [
      `Invoice #: ${meta?.invoiceNumber ?? '—'}`,
      `Date: ${this.formatDate(meta?.invoiceDate)}`,
      `Due: ${this.formatDate(meta?.invoiceDueDate)}`,
      '',
      'From: ' + (invoice.companyInfo?.name ?? '—'),
      'To: ' + (invoice.clientInfo?.name ?? '—'),
      '',
      `Subtotal: ${this.formatCurrency(invoice.financialDetails?.subtotal)}`,
      `Tax: ${this.formatCurrency(invoice.financialDetails?.taxAmount)}`,
      `Total: ${this.formatCurrency(invoice.financialDetails?.totalAmount)}`,
      `Status: ${invoice.status ?? '—'}`,
    ];
    if (meta?.notes?.trim()) {
      lines.push('', 'Notes: ' + meta.notes.trim());
    }
    return lines.join('\n');
  }

  formatCurrency(value: number | undefined): string {
    if (value == null || Number.isNaN(value)) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  }

  formatAddress(addr: { address?: string; city?: string; province?: string; postalCode?: string; country?: string } | undefined): string {
    if (!addr) return '—';
    const parts = [addr.address, addr.city, addr.province, addr.postalCode, addr.country].filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  }
}
